// src/components/pipeline/PipelineBoardView.jsx
// Board view: one column per stage (parked stages muted at the end), cards
// clickable into the lead drawer. Airy minimal look — white page, plain lanes
// with a 2px colored underline per stage (stageColor prop). Cards can be
// DRAGGED between columns (dnd-kit, same PointerSensor distance-8 setup as the
// Deals Board so plain clicks still open the drawer); the stage dropdown stays
// as the accessible alternative. Attention leads (active, unparked, nothing
// scheduled) get an amber dot after the name + a quiet "Needs a follow-up"
// line at the card bottom. renderDetails adds per-pipeline muted detail lines.
// Spec: docs/superpowers/specs/2026-07-28-pipeline-board-list-redesign.md
import React, { useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { FileText } from "lucide-react";
import LeadCardMenu from "./LeadCardMenu";
import { groupLeadsByStage, needsAttentionLead } from "../../utils/pipelineView";
import { formatDate } from "../../utils/helper";
import { clientNames } from "../../utils/leadClients";
import { useStateValue } from "../../context/StateProvider";
import { canUseTexting, useUnreadTexts } from "../../utils/texting";
import { getRatingColor } from "../../utils/buyerActivity";

// Unread-texts chip — self-contained so every card gets it for free.
// All instances share ONE Firestore listener (see useUnreadTexts).
const UnreadTextsChip = ({ leadId }) => {
  const [{ user }] = useStateValue();
  const unread = useUnreadTexts(canUseTexting(user));
  const n = canUseTexting(user) ? unread.leads[leadId] || 0 : 0;
  if (!n) return null;
  return (
    <span className="text-[10px] font-bold text-white bg-themeColor rounded-full px-1.5 py-0.5 whitespace-nowrap">
      💬 {n}
    </span>
  );
};

// Colours come from the shared map, not a copy — this file kept its own and
// drifted to indigo months after the brand sweep moved C to slate.
const RatingPill = ({ rating }) => {
  const r = String(rating || "").toUpperCase();
  const style = getRatingColor(r);
  if (!style) return null;
  return (
    <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${style}`}>
      {r}
    </span>
  );
};

const NameAndBadges = ({ lead, wonLabel, attention }) => (
  <span className="flex flex-wrap items-center gap-1.5 min-w-0">
    <span className="font-semibold text-[13.5px] text-slate-900 truncate">
      {clientNames(lead) || lead.contactName || "(unnamed lead)"}
    </span>
    {attention && (
      <span
        className="size-1.5 rounded-full bg-amber-500 inline-block flex-shrink-0"
        title="Needs a follow-up"
      />
    )}
    <RatingPill rating={lead.rating} />
    <UnreadTextsChip leadId={lead.id} />
    {lead.status === "converted" && (
      <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 rounded px-1.5 py-0.5 whitespace-nowrap">
        ✓ {wonLabel}
      </span>
    )}
    {lead.status === "lost" && (
      <span className="text-[10px] font-bold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">
        Lost
      </span>
    )}
    {lead.fileRequested && (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 whitespace-nowrap">
        <FileText size={10} /> File
      </span>
    )}
  </span>
);

// stopPropagation on pointerdown too, so pressing the select / attention
// buttons can never start a card drag.
const stopPointer = (e) => e.stopPropagation();

const StageSelect = ({ lead, stages, stageOptions, onStageChange }) => (
  <select
    value={lead.stage || stages[0]}
    onPointerDown={stopPointer}
    onClick={(e) => e.stopPropagation()}
    onChange={(e) => {
      e.stopPropagation();
      onStageChange?.(lead, e.target.value);
    }}
    className="block w-full text-[11px] rounded border border-slate-200 bg-slate-50 px-1.5 py-1 text-slate-600"
    title="Move to stage"
  >
    {(stageOptions || stages).map((s) => (
      <option key={s} value={s}>
        {s}
      </option>
    ))}
  </select>
);

const NextTouchLine = ({ lead, apptLabel }) => {
  if (String(lead.appointmentAt || "").trim())
    return (
      <span className="text-slate-500 whitespace-nowrap">
        {apptLabel} {formatDate(lead.appointmentAt)}
      </span>
    );
  if (String(lead.nextFollowUp || "").trim())
    return (
      <span className="text-slate-500 whitespace-nowrap">
        Follow-up {formatDate(lead.nextFollowUp)}
      </span>
    );
  return null;
};

// ONE quiet line at the card bottom for attention leads.
const AttentionLine = ({ lead, onOpen, canDismiss, onDismiss }) => (
  <p className="flex flex-wrap items-center gap-1 text-[11px] text-amber-700">
    Needs a follow-up
    <span aria-hidden="true">·</span>
    <button
      type="button"
      onPointerDown={stopPointer}
      onClick={(e) => {
        e.stopPropagation();
        onOpen?.(lead);
      }}
      className="underline underline-offset-2 hover:text-amber-900"
    >
      set date
    </button>
    {canDismiss && (
      <>
        <span aria-hidden="true">·</span>
        <button
          type="button"
          title="Snoozes this lead one week out"
          onPointerDown={stopPointer}
          onClick={(e) => {
            e.stopPropagation();
            onDismiss(lead);
          }}
          className="underline underline-offset-2 hover:text-amber-900"
        >
          snooze
        </button>
      </>
    )}
  </p>
);

// Inner card content — shared by the in-column card and the DragOverlay clone.
const CardContent = ({
  lead,
  attention,
  stages,
  stageOptions,
  wonLabel,
  apptLabel,
  renderSecondary,
  renderDetails,
  canManage,
  onOpen,
  onStageChange,
  onDismiss,
  onMarkLost,
  onDelete,
}) => (
  <>
    <span className="flex items-start gap-1">
      <NameAndBadges lead={lead} wonLabel={wonLabel} attention={attention} />
      {canManage?.(lead) ? (
        <span className="ml-auto -mr-1 -mt-0.5">
          <LeadCardMenu lead={lead} onMarkLost={onMarkLost} onDelete={onDelete} />
        </span>
      ) : null}
    </span>
    {renderSecondary?.(lead) ? (
      <p className="text-[12.5px] text-slate-500 truncate">
        {renderSecondary(lead)}
      </p>
    ) : null}
    {(renderDetails?.(lead) || [])
      .filter((line) => String(line || "").trim())
      .map((line, i) => (
        <p key={i} className="text-[11px] text-slate-500 truncate">
          {line}
        </p>
      ))}
    <div className="space-y-1.5 text-[12px]">
      {attention ? (
        <AttentionLine
          lead={lead}
          onOpen={onOpen}
          canDismiss={Boolean(onDismiss && canManage?.(lead))}
          onDismiss={onDismiss}
        />
      ) : (
        <NextTouchLine lead={lead} apptLabel={apptLabel} />
      )}
      {canManage?.(lead) ? (
        <StageSelect
          lead={lead}
          stages={stages}
          stageOptions={stageOptions}
          onStageChange={onStageChange}
        />
      ) : null}
    </div>
  </>
);

// A card in its column: draggable for managers (drag disabled otherwise, so
// non-managers get no drag listeners), always clickable into the drawer.
const BoardCard = ({ lead, parkingStages, ...rest }) => {
  const attention = needsAttentionLead(lead, { parkingStages });
  const draggable = Boolean(rest.canManage?.(lead));
  const { setNodeRef, listeners, isDragging } = useDraggable({
    id: String(lead.id),
    data: { lead },
    disabled: !draggable,
  });
  return (
    <div
      ref={setNodeRef}
      role="button"
      tabIndex={0}
      {...(draggable && listeners ? listeners : {})}
      onClick={() => {
        if (!isDragging) rest.onOpen?.(lead);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") rest.onOpen?.(lead);
      }}
      className={`bg-white border border-slate-200 rounded-xl p-3 space-y-1 hover:shadow-md transition cursor-pointer ${
        lead.status !== "active" ? "opacity-60" : ""
      } ${isDragging ? "opacity-40" : ""}`}
    >
      <CardContent lead={lead} attention={attention} {...rest} />
    </div>
  );
};

// Column-level drop target (no within-column reordering — order stays the
// computed attention-first order). Ring while a drag hovers it.
const ColumnDroppable = ({ stage, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id: String(stage) });
  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col gap-2 rounded-lg min-h-[60px] transition ${
        isOver ? "ring-2 ring-themeColor" : ""
      }`}
    >
      {children}
    </div>
  );
};

export default function PipelineBoardView({
  leads,
  stages,
  parkingStages = [],
  stageDot, // (stage) => JSX dot in the stage's color
  stageColor, // (stage) => Tailwind bg-* class for the column underline
  stageOptions,
  graduationStage,
  wonLabel = "Won",
  apptLabel = "Appt",
  renderSecondary, // (lead) => string/JSX — the per-pipeline secondary line
  renderDetails, // (lead) => array of short strings — muted detail lines
  canManage,
  onOpen,
  onStageChange,
  onDismiss, // (lead) => void — snoozes the lead's follow-up a week out
  onMarkLost, // (lead) => void — optional; ⋮ menu "Mark as Lost" on manageable cards
  onDelete, // (lead) => void — optional; ⋮ menu "Delete Lead" on manageable cards
}) {
  const [activeLead, setActiveLead] = useState(null);

  // Distance-based activation (mirrors the Deals Board): a click is "press and
  // release without moving" — drag only kicks in after the pointer travels 8px,
  // so plain clicks still open the drawer.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const columns = groupLeadsByStage(leads, stages, { parkingStages });
  const columnIds = new Set(columns.map((c) => String(c.stage)));

  const handleDragStart = ({ active }) =>
    setActiveLead(active.data.current?.lead || null);

  const handleDragCancel = () => setActiveLead(null);

  const handleDragEnd = ({ active, over }) => {
    setActiveLead(null);
    if (!over) return;
    const lead = active.data.current?.lead;
    const target = String(over.id);
    if (!lead || !columnIds.has(target)) return;
    // Dropping on the card's own column is a no-op.
    if (target === String(lead.stage || stages[0] || "")) return;
    onStageChange?.(lead, target);
  };

  const cardProps = {
    stages,
    stageOptions,
    wonLabel,
    apptLabel,
    renderSecondary,
    renderDetails,
    canManage,
    onOpen,
    onStageChange,
    onDismiss,
    onMarkLost,
    onDelete,
  };

  if (!columns.length)
    return (
      <div className="bg-white border border-slate-200 rounded-xl">
        <p className="px-4 py-8 text-center text-sm text-slate-400">
          No leads match this view.
        </p>
      </div>
    );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      autoScroll
    >
      <div className="flex items-start gap-4 overflow-x-auto pb-2">
        {columns.map(({ stage, rows, parked }) => (
          <div
            key={stage}
            className={`w-[300px] flex-shrink-0 ${parked ? "opacity-60" : ""}`}
          >
            <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 px-1 mb-1">
              {stageDot?.(stage)} {stage}
              <span className="font-normal text-slate-400">{rows.length}</span>
              {stage === graduationStage && (
                <span className="text-[9px] font-bold text-linkColor bg-slate-50 rounded px-1 py-0.5 normal-case">
                  → Deals Board
                </span>
              )}
            </p>
            <div
              className={`h-0.5 rounded mb-2 ${
                stageColor ? stageColor(stage) : "bg-slate-300"
              }`}
            />
            <ColumnDroppable stage={stage}>
              {rows.length ? (
                rows.map((lead) => (
                  <BoardCard
                    key={lead.id}
                    lead={lead}
                    parkingStages={parkingStages}
                    {...cardProps}
                  />
                ))
              ) : (
                <p className="text-[12px] text-slate-400 px-1 py-2">No leads</p>
              )}
            </ColumnDroppable>
          </div>
        ))}
      </div>
      {/* Floating clone of the dragged card */}
      <DragOverlay>
        {activeLead ? (
          <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-1 shadow-lg cursor-grabbing">
            <CardContent
              lead={activeLead}
              attention={needsAttentionLead(activeLead, { parkingStages })}
              {...cardProps}
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
